import CustomDropdown from "@/components/Dropdown/Dropdown";
import Spinner from "@/components/Spinner/Spinner";
import { Button } from "@/components/UiElements";
import addPricing from "@/services/ProductService/addPricing";
import updatePricing from "@/services/ProductService/updatePricing";
import { DiscountModel, DiscountType, PriceModel, Pricing } from "@/types/pricing";
import { ChangeEvent, useState } from "react";
import { toast } from "react-toastify";
import notifyError from "@/utils/notifyError";

type PricingFormProps = {
  oldPrice?: Pricing
  onUpdateSuccess?: (updatedPrice: Pricing) => void
  closeModal?:  () => void
}

const discountModelOptions = [
  { label: 'None', value: DiscountModel.NONE },
  { label: 'Fixed', value: DiscountModel.FIXED },
  { label: 'Discount on addition', value: DiscountModel.DISCOUNT_ON_ADDITION },
];

const priceModelOptions = [
  { label: 'Element', value: PriceModel.ELEMENT },
  { label: 'Fixed', value: PriceModel.FIXED },
  { label: 'Section', value: PriceModel.SECTION },
  { label: 'Size', value: PriceModel.SIZE },
];

const discountTypeOptions = [
  { label: 'Percent', value: DiscountType.PERCENT },
  { label: 'Value', value: DiscountType.VALUE  }
];

const PricingForm = ({
                       oldPrice,
                       onUpdateSuccess,
                       closeModal
                     }: PricingFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState(oldPrice?.price || 1);
  const [name, setName] = useState(oldPrice?.name || '');
  const [priceModel, setPriceModel] = useState(oldPrice?.price_model || PriceModel.ELEMENT);
  const [discount, setDiscount] = useState(oldPrice?.discount || 0);
  const [discountType, setDiscountType] = useState(oldPrice?.discount_type || DiscountType.VALUE);
  const [discountModel, setDiscountModel] = useState(oldPrice?.discount_model || DiscountModel.FIXED);
  const [minPriceTotal, setMinPriceTotal] = useState(oldPrice?.min_price_total || 1);
  const [maxPriceTotal, setMaxPriceTotal] = useState(oldPrice?.max_price_total || 1);

  const handleCreatePricing = async () => {
    if (price < 0) {
      notifyError(`Price ${price} cannot be less than zero.`);
      return;
    }
    if (name ==='') {
      notifyError(`Name cannot be empty.`);
      return;
    }

    if (minPriceTotal > maxPriceTotal) {
      notifyError(`Min price ${minPriceTotal} cannot be higher than max price ${maxPriceTotal}.`);
      return;
    }

    setIsLoading(true)
    await addPricing(
        priceModel,
        price,
        discount,
        discountType,
        discountModel,
        maxPriceTotal,
        minPriceTotal,
        name
    );
    toast.success('Pricing added successfully')
    setIsLoading(false)
    closeModal && closeModal()
  }

  const handleEditPricing = async () => {
    if (!oldPrice?.id) {
      return;
    }
    if (name ==='') {
      notifyError(`Name cannot be empty.`);
      return;
    }
    if (price < 0) {
      notifyError(`Price ${price} cannot be less than zero.`);
      return;
    }

    if (minPriceTotal > maxPriceTotal) {
      notifyError(`Min price ${minPriceTotal} cannot be higher than max price ${maxPriceTotal}.`);
      return;
    }

    setIsLoading(true)
    const pricing: Pricing = {
      id: oldPrice?.id,
      price_model: priceModel,
      price,
      discount,
      discount_type: discountType,
      discount_model: discountModel,
      max_price_total: maxPriceTotal,
      min_price_total: minPriceTotal,
      name: name,
    }
    await updatePricing(pricing);
    onUpdateSuccess && onUpdateSuccess(pricing)
    toast.success('Pricing updated successfully')
    setIsLoading(false)
    closeModal && closeModal()
  }

  return (
      <div className={'flex flex-col justify-center'}>
        {/*<h2 className="text-lg font-extrabold my-3">Price</h2>*/}
        <div className="flex justify-start">
          <div className='w-fit my-3'>
            <p>Price</p>
            <input
                type='number'
                name='price'
                id='price'
                placeholder='1'
                value={price}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPrice(Number(e.target.value))}
                className='transition-all outline-none
              w-full text-text-md text-gray-900 rounded-md border border-gray-300 bg-white
              placeholder:text-gray-400 placeholder:text-text-md
              focus:shadow-focus focus:border-primary-500
              hover:border-primary-500 shadow-xs
              h-[44px] px-md'
            />
          </div>
          <div className='my-3 ml-3 w-[400px]'>
            <p>Price Model</p>
            <CustomDropdown
                options={priceModelOptions}
                initialSelectedOption={oldPrice ? priceModelOptions.findIndex(option => option.value === oldPrice?.price_model) : 0}
                onSelectedOptionChange={(option) => setPriceModel(option.value)}
                hasMore={false}
            />
          </div>
        </div>
        <div className="flex justify-start">
          <div className='w-full my-3'>
            <p>Name</p>
            <input
                type='text'
                name='name'
                id='name'
                placeholder=''
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                className='transition-all outline-none
              w-full text-text-md text-gray-900 rounded-md border border-gray-300 bg-white
              placeholder:text-gray-400 placeholder:text-text-md
              focus:shadow-focus focus:border-primary-500
              hover:border-primary-500 shadow-xs
              h-[44px] px-md'
            />
          </div>
        </div>
        <h2 className="text-lg font-extrabold my-3">Discount</h2>
        <div className='flex my-3 w-full'>
          <div>
            <p>Discount</p>
            <input
                type='number'
                name='discount'
                id='discount'
                placeholder='0'
                value={discount}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDiscount(Number(e.target.value))}
                className='transition-all outline-none
              w-full text-text-md text-gray-900 rounded-md border border-gray-300 bg-white
              placeholder:text-gray-400 placeholder:text-text-md
              focus:shadow-focus focus:border-primary-500
              hover:border-primary-500 shadow-xs
              h-[44px] px-md'
            />
          </div>
          <div className="ml-3 w-[400px]">
            <p>Discount Type</p>
            <CustomDropdown
                options={discountTypeOptions}
                initialSelectedOption={oldPrice ? discountTypeOptions.findIndex(option => option.value === oldPrice?.discount_type) : 0}
                onSelectedOptionChange={(option) => {console.log(option);setDiscountType(option.value)}} hasMore={false}
            />
          </div>
          <div className="ml-3 w-[400px]">
            <p>Discount Model</p>
            <CustomDropdown
                options={discountModelOptions}
                initialSelectedOption={oldPrice ? discountModelOptions.findIndex(option => option.value === oldPrice?.discount_model) : 0}
                onSelectedOptionChange={(option) => setDiscountModel(option.value)} hasMore={false}
            />
          </div>
        </div>
        <div className='flex justify-start my-3'>
          <div>
            <p>Min Price Total</p>
            <input
                type='number'
                name='price'
                id='price'
                placeholder='1'
                value={minPriceTotal}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setMinPriceTotal(Number(e.target.value))}
                className='transition-all outline-none
              w-full text-text-md text-gray-900 rounded-md border border-gray-300 bg-white
              placeholder:text-gray-400 placeholder:text-text-md
              focus:shadow-focus focus:border-primary-500
              hover:border-primary-500 shadow-xs
              h-[44px] px-md'
            />
          </div>
          <div className="ml-3">
            <p>Max Price Total</p>
            <input
                type='number'
                name='price'
                id='price'
                placeholder='1'
                value={maxPriceTotal}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setMaxPriceTotal(Number(e.target.value))}
                className='transition-all outline-none
              w-full text-text-md text-gray-900 rounded-md border border-gray-300 bg-white
              placeholder:text-gray-400 placeholder:text-text-md
              focus:shadow-focus focus:border-primary-500
              hover:border-primary-500 shadow-xs
              h-[44px] px-md'
            />
          </div>
        </div>
        <div className="flex justify-end gap-md">
          {closeModal && (
            <Button disabled={isLoading} type="alt" onClick={closeModal}>
              Cancel
            </Button>
          )}
          <Button disabled={isLoading} onClick={oldPrice ? handleEditPricing : handleCreatePricing}>
            {isLoading ? <Spinner /> : (oldPrice ? 'Set pricing' : 'Add pricing')}
          </Button>
        </div>
      </div>
  )
}

export default PricingForm
